import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { client } from '../../networking/client';
import { RootState } from '../store';

export interface IWorkOrderServiceDiagnostic {
  work_order_id: number;
  service_id: number;
  sample_taken_id: number;
  diagnostic_validation_employee_id: number;
  diagnostic_business_partner_id: number;
  diagnostic_method_id: number;
  positive_negative?: string;
  appointment_id?: number;
}

export interface WorkOrderServiceDiagnosticState {
  createStatus: string;
  createdWorkOrderServiceDiagnostic: IWorkOrderServiceDiagnostic | null;
  errorCode: number | null;
}

const initialState: WorkOrderServiceDiagnosticState = {
  createStatus: 'idle',
  createdWorkOrderServiceDiagnostic: null,
  errorCode: null,
};

interface CreateWorkOrderServiceDiagnosticBody {
  salesOrderId: number;
  salesOrderItemId: number;
  serviceId: number;
  employeeId: number;
  businessPartnerId: string;
  sampleBarcode: string;
  isOutsideLaboratory: boolean;
  personEmail: string;
  personAccountNumber: string;
  taId: number;
  transactionId: number;
}

export const createWorkOrderServiceDiagnostic = createAsyncThunk(
  'work-order-service-diagnostic/create',
  async (
    createWorkOrderServiceDiagnosticBody: CreateWorkOrderServiceDiagnosticBody,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state: any = getState();
      const createWorkServiceResponse: any = await client.post(
        `/healthpoint/work-order-service-diagnostic/${state?.auth?.accountInfo?.account_number}/${state?.auth?.basicAuth}`,
        {
          salesOrderId: createWorkOrderServiceDiagnosticBody.salesOrderId,
          salesOrderItemId: createWorkOrderServiceDiagnosticBody.salesOrderItemId,
          serviceId: createWorkOrderServiceDiagnosticBody.serviceId,
          employeeId: createWorkOrderServiceDiagnosticBody.employeeId,
          businessPartnerId: createWorkOrderServiceDiagnosticBody.businessPartnerId,
          sampleBarcode: createWorkOrderServiceDiagnosticBody.sampleBarcode,
          isOutsideLaboratory: createWorkOrderServiceDiagnosticBody.isOutsideLaboratory,
          personEmail: createWorkOrderServiceDiagnosticBody.personEmail,
          personAccountNumber: createWorkOrderServiceDiagnosticBody.personAccountNumber,
          taId: createWorkOrderServiceDiagnosticBody.taId,
          transactionId: createWorkOrderServiceDiagnosticBody.transactionId,
        }
      );
      return createWorkServiceResponse;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? {});
    }
  }
);

export const workOrderServiceDiagnosticSlice = createSlice({
  name: 'workOrderServiceDiagnostic',
  initialState,
  reducers: {
    resetWorkOrderService: (_) => {
      return initialState;
    },
    resetCreateStatus: (state) => {
      return {
        ...state,
        createStatus: 'idle',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createWorkOrderServiceDiagnostic.pending, (state) => {
        state.createStatus = 'pending';
      })
      .addCase(createWorkOrderServiceDiagnostic.fulfilled, (state, action) => {
        state.createStatus = 'fulfilled';
        state.createdWorkOrderServiceDiagnostic = action.payload.data?.workOrderServiceDiagnostic;
        // toast.success('Successfully checked-in.', {
        //   position: toast.POSITION.BOTTOM_RIGHT,
        // });
      })
      .addCase(createWorkOrderServiceDiagnostic.rejected, (state, action: any) => {
        state.errorCode = action.payload.error_code;
        state.createStatus = 'rejected';
      });
  },
});

export const { resetWorkOrderService, resetCreateStatus } = workOrderServiceDiagnosticSlice.actions;

export default workOrderServiceDiagnosticSlice.reducer;

export const selectWorkOrderServiceDiagnosticCreateStatus = (state: RootState) =>
  state.workOrderServiceDiagnostic.createStatus;
export const selectWorkOrderServiceDiagnosticErrorCode = (state: RootState) =>
  state.workOrderServiceDiagnostic.errorCode;
export const selectCreatedWorkOrderServiceDiagnostic = (state: RootState) =>
  state.workOrderServiceDiagnostic.createdWorkOrderServiceDiagnostic;
